import { getLogger } from '@packages/core/logging';

import type { RetailerProfileOption } from '../types';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { toRetailerProfileOption } from '../helpers/mappers';

const logger = getLogger('techline.members');

/** Parameters for fetching retailer profile options. */
export type FetchRetailerProfileOptionsParams = {
    /** The retailer number to filter profile options by. */
    retailerNumber?: string;
};

/**
 * Fetch retailer profile options for the authenticated user. Each profile option represents a
 * retailer the user is associated with and includes the retailer number. A user
 * can only create a Techline case under a retailer they have a profile option for.
 * Typically a user is associated with a single retailer. When a retailer number
 * is provided, returns profile options for that specific retailer; otherwise returns
 * all profile options for the authenticated user.
 */
export class FetchRetailerProfileOptions extends StsBackendOperation {
    /**
     * Fetch retailer profile options associated with the authenticated user or a specific retailer number.
     *
     * @param params - retailer number to filter profile options by, or undefined to fetch all profile options for the user
     * @param signal - optional abort signal for request cancellation
     * @returns the list of retailer profile options matching the request criteria
     */
    async exec(
        params: FetchRetailerProfileOptionsParams,
        signal?: AbortSignal
    ): Promise<RetailerProfileOption[] | undefined> {
        const { retailerNumber } = params;
        try {
            const response = await this.client.getRetailerProfiles({ retailerNumber }, signal);

            if (!response.success || !response.data) {
                return undefined;
            }

            const results = response.data.results;

            if (!results?.length) {
                return [];
            }

            return results.map(toRetailerProfileOption);
        } catch (e) {
            logger.error('Failed to fetch the retailer profile options', { error: e });
        }
    }
}
